import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";

export const SlideshowViewer = ({ slides = [], title = "", autoPlay = true, interval = 6000 }) => {
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(autoPlay);

  const next = useCallback(() => {
    setCurrent(prev => (prev + 1) % slides.length);
  }, [slides.length]);

  const prev = useCallback(() => {
    setCurrent(prev => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    if (!playing || slides.length <= 1) return;
    const timer = setInterval(next, interval);
    return () => clearInterval(timer);
  }, [playing, next, interval, slides.length]);

  if (!slides.length) return null;

  const slide = slides[current];

  return (
    <div className="rounded-xl overflow-hidden shadow-lg" data-testid="slideshow-viewer">
      {title && (
        <div className="bg-slate-900 px-4 py-2 flex items-center justify-between">
          <span className="text-white text-sm font-semibold tracking-wide">{title}</span>
          <span className="text-slate-400 text-xs">{current + 1} / {slides.length}</span>
        </div>
      )}
      <div
        className="relative transition-colors duration-500"
        style={{ backgroundColor: slide.bg_color || "#1e293b", minHeight: "280px" }}
      >
        <div className="px-8 py-10 md:px-12 md:py-14 flex flex-col justify-center min-h-[280px]">
          {slide.title && (
            <h3
              className="text-xl md:text-2xl font-bold mb-4 font-serif"
              style={{ color: slide.text_color || "#fff" }}
            >
              {slide.title}
            </h3>
          )}
          {slide.content && (
            <p
              className="text-base md:text-lg leading-relaxed opacity-90"
              style={{ color: slide.text_color || "#fff" }}
            >
              {slide.content}
            </p>
          )}
        </div>

        {/* Navigation arrows */}
        {slides.length > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white transition-colors"
              data-testid="slideshow-prev"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white transition-colors"
              data-testid="slideshow-next"
              aria-label="Next slide"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>

      {/* Bottom controls */}
      {slides.length > 1 && (
        <div className="bg-slate-900 px-4 py-2.5 flex items-center justify-center gap-3">
          <button
            onClick={() => setPlaying(p => !p)}
            className="text-slate-400 hover:text-white transition-colors"
            data-testid="slideshow-play-pause"
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <div className="flex gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === current ? "bg-white w-5" : "bg-slate-600 hover:bg-slate-400"
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SlideshowViewer;
